import React, { useState, useMemo } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import GalleryModal from '../components/GalleryModal';
import { galleryImages, galleryCategories } from '../data/siteData';

export default function GalleryPage() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [modalOpen, setModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const filteredImages = useMemo(() => {
        if (activeCategory === 'All') return galleryImages;
        return galleryImages.filter(img => img.category === activeCategory);
    }, [activeCategory]);

    const openModal = (index) => {
        setCurrentIndex(index);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const navigateModal = (index) => setCurrentIndex(index);

    return (
        <main>
            {/* Hero */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <img src="/images/surroundings/lake.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
                <div className="relative z-10 container-custom text-center px-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                        <span className="text-white/80 font-sans text-xs tracking-widest uppercase">Visual Stories</span>
                    </div>
                    <h1 className="heading-display text-white mb-4">
                        Our <span className="italic text-gold-300">Gallery</span>
                    </h1>
                    <p className="font-body text-xl text-white/60 max-w-xl mx-auto">
                        A glimpse into the beauty, comfort, and experiences that await you at UC Eden Valley.
                    </p>
                </div>
            </section>

            {/* Gallery */}
            <section className="section-padding">
                <div className="container-custom">
                    {/* Category Filter */}
                    <AnimatedSection className="flex flex-wrap justify-center gap-3 mb-12">
                        {galleryCategories.map(category => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-5 py-2.5 rounded-xl font-sans text-sm font-medium tracking-wide transition-all duration-300 ${activeCategory === category
                                    ? 'bg-gold-500 text-white shadow-lg shadow-gold-500/25'
                                    : 'bg-gray-100 dark:bg-royal-600/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-royal-600 border border-gray-200/50 dark:border-gold-500/10'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </AnimatedSection>

                    {/* Image count */}
                    <p className="text-center text-sm font-sans text-gray-500 dark:text-gray-400 mb-8">
                        Showing {filteredImages.length} {filteredImages.length === 1 ? 'photo' : 'photos'}
                        {activeCategory !== 'All' && ` in ${activeCategory}`}
                    </p>

                    {/* Gallery Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                        {filteredImages.map((image, index) => (
                            <AnimatedSection key={image.id} delay={Math.min(index * 0.05, 0.5)}>
                                <button
                                    onClick={() => openModal(index)}
                                    className="group relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 dark:bg-royal-600/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 dark:focus:ring-offset-royal-500"
                                >
                                    {/* Real image */}
                                    <img
                                        src={image.src}
                                        alt={image.alt}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />

                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-5">
                                        <p className="text-white font-body text-sm italic transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                            {image.caption}
                                        </p>
                                        <span className="inline-flex items-center gap-1 text-gold-300 font-sans text-xs mt-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500" style={{ transitionDelay: '0.05s' }}>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                                            </svg>
                                            View
                                        </span>
                                    </div>

                                    {/* Category badge */}
                                    <div className="absolute top-3 left-3">
                                        <span className="px-2.5 py-1 rounded-lg bg-black/40 backdrop-blur-sm text-white/80 font-sans text-[10px] tracking-wider uppercase">
                                            {image.category}
                                        </span>
                                    </div>
                                </button>
                            </AnimatedSection>
                        ))}
                    </div>

                    {filteredImages.length === 0 && (
                        <div className="text-center py-20">
                            <p className="font-body text-xl text-gray-400 dark:text-gray-500 italic">
                                No photos in this category yet.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* Modal */}
            <GalleryModal
                images={filteredImages}
                currentIndex={currentIndex}
                isOpen={modalOpen}
                onClose={closeModal}
                onNavigate={navigateModal}
            />
        </main>
    );
}
